import {
    BottomNavigation,
    BottomNavigationAction,
    Box,
    Container,
    Divider,
    Paper,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import VersionDialogButton from '@src/App/Components/Dialogs/VersionDialog/VersionDialog';
import type { AppState } from '@store/store';
import { Info, Mail } from 'lucide-react';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

export function Footer() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const lang = useSelector((state: AppState) => state.employee.language);
    const employee = useSelector((state: AppState) => state.employee);
    const isLoggedIn = employee && employee.id > 0;
    const langStrings = React.useMemo(() => getLanguageTexts(lang).layout, [lang]);

    const navigate = useNavigate();
    const [value, setValue] = React.useState(0);

    const handleImprint = () => {
        navigate(`/${ELinks.IMPRESSUM}`);
    };

    const handleContact = () => {
        navigate(`/${ELinks.KONTAKT}`);
    };

    const handlePrivacy = () => {
        navigate(`/${ELinks.DATENSCHUTZ}`);
    };

    // Für mobile Geräte verwenden wir BottomNavigation
    if (isMobile) {
        return (
            <Paper
                elevation={0}
                sx={{
                    width: '100%',
                    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                    borderRadius: 0,
                }}
            >
                <BottomNavigation
                    showLabels
                    value={value}
                    onChange={(event, newValue) => {
                        setValue(newValue);
                    }}
                    sx={{
                        height: 56,
                    }}
                >
                    <BottomNavigationAction
                        label=""
                        icon={<Typography variant="caption">© {new Date().getFullYear()}</Typography>}
                        disabled
                    />
                    <BottomNavigationAction label="" icon={<Info size={20} />} onClick={handleImprint} />
                    <BottomNavigationAction label="" icon={<Mail size={20} />} onClick={handleContact} />
                    <BottomNavigationAction label="" icon={<VersionDialogButton />} />
                </BottomNavigation>
            </Paper>
        );
    }

    // Für Desktop-Geräte verwenden wir eine angepasste Version
    return (
        <Paper
            component="footer"
            elevation={0}
            sx={{
                width: '100%',
                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: 0,
            }}
        >
            <Container maxWidth="xl">
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        height: 56,
                    }}
                >
                    {/* Copyright Section */}
                    <Typography variant="caption">© {new Date().getFullYear()} Talentillion GmbH</Typography>

                    {/* Links Section */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                            variant="caption"
                            component="span"
                            onClick={handleImprint}
                            sx={{ cursor: 'pointer' }}
                        >
                            {langStrings.imprint}
                        </Typography>

                        <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 16 }} />

                        <Typography
                            variant="caption"
                            component="span"
                            onClick={handleContact}
                            sx={{ cursor: 'pointer' }}
                        >
                            {langStrings.contact}
                        </Typography>

                        <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 16 }} />

                        <Typography
                            variant="caption"
                            component="span"
                            onClick={handlePrivacy}
                            sx={{ cursor: 'pointer' }}
                        >
                            Datenschutz
                        </Typography>

                        {isLoggedIn && (
                            <>
                                <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 16 }} />

                                <Typography
                                    variant="caption"
                                    component="span"
                                    onClick={() => navigate(`/${ELinks.COMPANY_REGISTRATION}`)}
                                    sx={{ cursor: 'pointer', color: 'primary.main' }}
                                >
                                    Test Registration
                                </Typography>

                                <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 16 }} />
                            </>
                        )}

                        {!isLoggedIn && <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 16 }} />}

                        <VersionDialogButton />
                    </Box>
                </Box>
            </Container>
        </Paper>
    );
}
